/**
 * techTreeSearch — pure tree-building + search for the Solutions page tech-tree
 * picker (TechTreePickerModal). Kept free of React so it can be unit-tested.
 *
 * The backend (`GET /api/technologies/picker-tree/`) returns the taxonomy as a
 * flat list; this builds Discipline → root tech → subtech nodes and runs a light
 * fuzzy search over it: every query token must hit the node's name (substring,
 * or a word / word-prefix within a small edit distance to tolerate typos,
 * plurals and abbreviations) or its description (substring). A search yields the matching nodes plus their
 * ancestors, so the tree can be pruned to show *where* each match lives.
 */

export interface PickerTech {
  id: number
  name: string
  description: string
  parent_id: number | null
  discipline_id: number | null
  product_count: number
}

export interface PickerDiscipline {
  id: number
  name: string
}

export interface PickerTreeResponse {
  disciplines: PickerDiscipline[]
  technologies: PickerTech[]
}

export interface PickerNode {
  /** `d:<id>` for disciplines (`d:none` for uncategorized roots), `t:<id>` for techs. */
  key: string
  kind: 'discipline' | 'tech'
  /** Technology id; null for discipline nodes. */
  techId: number | null
  name: string
  description: string
  parentKey: string | null
  children: PickerNode[]
  /** Active products tagged anywhere in this node's subtree. */
  subtreeProductCount: number
}

export interface PickerTree {
  roots: PickerNode[]
  byKey: Map<string, PickerNode>
}

export const UNCATEGORIZED_KEY = 'd:none'

const byName = (a: PickerNode, b: PickerNode) => a.name.localeCompare(b.name)

export function buildPickerTree(data: PickerTreeResponse): PickerTree {
  const byKey = new Map<string, PickerNode>()
  const techNodes = new Map<number, PickerNode>()
  const directCounts = new Map<string, number>()

  for (const t of data.technologies) {
    const node: PickerNode = {
      key: `t:${t.id}`,
      kind: 'tech',
      techId: t.id,
      name: t.name,
      description: t.description,
      parentKey: null,
      children: [],
      subtreeProductCount: 0,
    }
    techNodes.set(t.id, node)
    byKey.set(node.key, node)
    directCounts.set(node.key, t.product_count)
  }

  const disciplineNodes = new Map<string, PickerNode>()
  const disciplineNode = (key: string, name: string): PickerNode => {
    let node = disciplineNodes.get(key)
    if (!node) {
      node = {
        key,
        kind: 'discipline',
        techId: null,
        name,
        description: '',
        parentKey: null,
        children: [],
        subtreeProductCount: 0,
      }
      disciplineNodes.set(key, node)
      byKey.set(key, node)
    }
    return node
  }
  const disciplineName = new Map(data.disciplines.map((d) => [d.id, d.name]))

  for (const t of data.technologies) {
    const node = techNodes.get(t.id)!
    const parent = t.parent_id != null ? techNodes.get(t.parent_id) : undefined
    if (parent) {
      node.parentKey = parent.key
      parent.children.push(node)
      continue
    }
    // Root tech: group under its discipline (only top-level techs carry one).
    const discName = t.discipline_id != null ? disciplineName.get(t.discipline_id) : undefined
    const disc =
      discName != null
        ? disciplineNode(`d:${t.discipline_id}`, discName)
        : disciplineNode(UNCATEGORIZED_KEY, 'Uncategorized')
    node.parentKey = disc.key
    disc.children.push(node)
  }

  const finalize = (node: PickerNode): number => {
    node.children.sort(byName)
    let total = directCounts.get(node.key) ?? 0
    for (const child of node.children) total += finalize(child)
    node.subtreeProductCount = total
    return total
  }

  const roots = [...disciplineNodes.values()].sort((a, b) => {
    // Uncategorized always last.
    if (a.key === UNCATEGORIZED_KEY) return 1
    if (b.key === UNCATEGORIZED_KEY) return -1
    return byName(a, b)
  })
  for (const root of roots) finalize(root)
  return { roots, byKey }
}

/** Ancestors of a node, root first (excludes the node itself). */
export function ancestorsOf(tree: PickerTree, key: string): PickerNode[] {
  const path: PickerNode[] = []
  let cur = tree.byKey.get(key)?.parentKey ?? null
  while (cur) {
    const node = tree.byKey.get(cur)
    if (!node) break
    path.unshift(node)
    cur = node.parentKey
  }
  return path
}

// ── Search ──────────────────────────────────────────────────────────

export function tokenize(query: string): string[] {
  return query
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
}

/** Levenshtein distance, bailing out once it must exceed `max`. */
export function editDistanceWithin(a: string, b: string, max: number): boolean {
  if (Math.abs(a.length - b.length) > max) return false
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i)
  for (let i = 1; i <= a.length; i++) {
    const cur = [i]
    let rowMin = i
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost)
      rowMin = Math.min(rowMin, cur[j])
    }
    if (rowMin > max) return false
    prev = cur
  }
  return prev[b.length] <= max
}

const typoBudget = (token: string) => (token.length >= 8 ? 2 : token.length >= 4 ? 1 : 0)

function tokenHitsName(token: string, nameLower: string, nameWords: string[]): boolean {
  if (nameLower.includes(token)) return true
  const budget = typoBudget(token)
  if (budget === 0) return false
  // Compare against the whole word and against its same-length prefix, so a
  // typo still matches a longer word ("sattelite" → "satellites",
  // "comms" → "communications").
  return nameWords.some(
    (w) =>
      editDistanceWithin(token, w, budget) ||
      (w.length > token.length && editDistanceWithin(token, w.slice(0, token.length), budget)),
  )
}

export type MatchKind = 'name' | 'description'

/** How a node matches the query tokens: every token must hit the name or the
 *  description. 'name' when they all hit the name; null when any token misses. */
export function matchNode(tokens: string[], name: string, description: string): MatchKind | null {
  if (tokens.length === 0) return null
  const nameLower = name.toLowerCase()
  const nameWords = tokenize(name)
  const descLower = description.toLowerCase()
  let allInName = true
  for (const token of tokens) {
    if (tokenHitsName(token, nameLower, nameWords)) continue
    if (descLower.includes(token)) {
      allInName = false
      continue
    }
    return null
  }
  return allInName ? 'name' : 'description'
}

export interface PickerSearchResult {
  /** Matching node keys → how they matched. */
  matches: Map<string, MatchKind>
  /** Matches plus every ancestor of a match — the pruned tree. */
  visible: Set<string>
  /** Ancestors of matches: auto-expanded (and children-filtered) while searching. */
  expand: Set<string>
}

export function searchPickerTree(tree: PickerTree, query: string): PickerSearchResult | null {
  const tokens = tokenize(query)
  if (tokens.length === 0) return null
  const matches = new Map<string, MatchKind>()
  const visible = new Set<string>()
  const expand = new Set<string>()
  for (const node of tree.byKey.values()) {
    const kind = matchNode(tokens, node.name, node.description)
    if (!kind) continue
    matches.set(node.key, kind)
    visible.add(node.key)
    let cur = node.parentKey
    while (cur && !expand.has(cur)) {
      expand.add(cur)
      visible.add(cur)
      cur = tree.byKey.get(cur)?.parentKey ?? null
    }
  }
  return { matches, visible, expand }
}

/** [start, end) ranges of exact token occurrences in `text`, merged, for highlighting. */
export function highlightRanges(text: string, tokens: string[]): [number, number][] {
  const lower = text.toLowerCase()
  const ranges: [number, number][] = []
  for (const token of tokens) {
    let from = 0
    for (;;) {
      const idx = lower.indexOf(token, from)
      if (idx === -1) break
      ranges.push([idx, idx + token.length])
      from = idx + token.length
    }
  }
  ranges.sort((a, b) => a[0] - b[0])
  const merged: [number, number][] = []
  for (const r of ranges) {
    const last = merged[merged.length - 1]
    if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1])
    else merged.push([r[0], r[1]])
  }
  return merged
}
